import {DateTime} from 'luxon';
import {ScheduleType} from 'common/src/modules/decisionTree/decisionTree.types';
import {UTCTimezone} from 'common/src/modules/timezones/timezones.consts';
const MAX_YEARS_THRESHOLD = 10;

type ScheduleOutputInput = {
  name?: string | null;
  dateRange?: {start?: string; end?: string} | null;
  daysOfWeek?: Array<{days?: number[]; startHour?: number; endHour?: number}> | null;
};

export default function validateScheduleOutput(
  output: ScheduleOutputInput,
  scheduleType: ScheduleType,
): string {
  if (!output.name || typeof output.name !== 'string' || output.name.trim() === '') {
    return 'Output name is required and must be a non-empty string.';
  }

  if (!output.dateRange && !output.daysOfWeek) {
    return 'Either dateRange or daysOfWeek is required.';
  }

  if (scheduleType === ScheduleType.DaysOfWeek) {
    if (output.dateRange != null) {
      return 'Schedule type is Days Of Week, so outputs must use daysOfWeek (not dateRange).';
    }

    if (!output.daysOfWeek || output.daysOfWeek.length === 0) {
      return 'Schedule type is Days Of Week, so daysOfWeek is required and must be non-empty.';
    }

    for (let i = 0; i < output.daysOfWeek.length; i++) {
      const entry = output.daysOfWeek[i];

      if (!entry || !entry.days || entry.days.length === 0) {
        return `daysOfWeek[${i}]: days array is required and must be non-empty.`;
      }

      const invalidDay = entry.days.find((d) => typeof d !== 'number' || d < 0 || d > 6);

      if (invalidDay !== undefined) {
        return `daysOfWeek[${i}]: each day must be an integer 0-6 (Sunday-Saturday).`;
      }

      const startMinute = (entry.startHour ?? 0) * 60;
      const endMinute = ((entry.endHour ?? 0) + 1) * 60;

      if (startMinute === undefined || typeof startMinute !== 'number' || startMinute % 60 !== 0) {
        return `daysOfWeek[${i}]: start_minute must be an integer multiple of 60.`;
      }

      if (endMinute === undefined || typeof endMinute !== 'number' || endMinute % 60 !== 0) {
        return `daysOfWeek[${i}]: end_minute must be an integer multiple of 60.`;
      }

      if (startMinute < 0 || startMinute >= endMinute || endMinute > 1440) {
        return `daysOfWeek[${i}]: require 0 <= start_minute < end_minute <= 1440.`;
      }
    }

    return '';
  }

  // ScheduleType.DateRange
  if (output.daysOfWeek != null && output.daysOfWeek.length > 0) {
    return 'Schedule type is Date Range, so outputs must use dateRange (not daysOfWeek).';
  }

  if (!output.dateRange || typeof output.dateRange !== 'object') {
    return 'Schedule type is Date Range, so dateRange with start and end is required.';
  }

  const {start, end} = output.dateRange;

  if (!start || !end || typeof start !== 'string' || typeof end !== 'string') {
    return 'dateRange start and end must be ISO date strings.';
  }

  const startDt = DateTime.fromISO(start, {zone: UTCTimezone, setZone: true}).toUTC();
  const endDt = DateTime.fromISO(end, {zone: UTCTimezone, setZone: true}).toUTC();

  if (!startDt.isValid) {
    return `dateRange start is not a valid ISO date: ${startDt.invalidReason ?? 'invalid'}.`;
  }

  if (!endDt.isValid) {
    return `dateRange end is not a valid ISO date: ${endDt.invalidReason ?? 'invalid'}.`;
  }

  if (endDt.year > DateTime.now().plus({years: MAX_YEARS_THRESHOLD}).year) {
    return 'dateRange end must be less than 10 years from now.';
  }

  if (startDt >= endDt) {
    return 'dateRange start must be before end.';
  }

  return '';
}
